/**
 * ROI calculator math.
 *
 * Pure functions; no React, no DOM. The /pricing page wraps this in
 * an interactive client component, and the test suite hits it directly.
 *
 * Model: the cost of *not* studying well isn't textbook dollars — it's
 * the chance of washing out of the FAA Academy. We surface three
 * comparisons that the audience actually weighs:
 *
 *   1. Direct cost. ModernATC vs. typical alternatives (Anki + Nolan
 *      textbook + pointSixtyFive PDFs vs. an AT-CTI prep course).
 *
 *   2. Hourly cost. Treating study time as a billable hour, total
 *      ModernATC outlay over the study window divided by hours
 *      studied — usually well under $1/hr.
 *
 *   3. Washout-cost framing. If our spaced-repetition method reduces
 *      personal washout risk even a single percentage point, the
 *      expected-value swing dwarfs any subscription cost. We surface
 *      the EV calculation transparently; users can dial the
 *      assumption.
 */

export type TierChoice = "trainee_yearly" | "pro_yearly";

export interface CalculatorInput {
  /** Months from purchase to Class Convening Date (or test date). */
  monthsUntilTest: number;
  /** Average daily study minutes. */
  dailyStudyMinutes: number;
  /** Tier the user is considering. */
  tier: TierChoice;
  /** Assumed washout-risk reduction in percentage points. Default 1 pt. */
  washoutRiskReductionPoints?: number;
  /** Annual salary post-certification, USD. Default $128k (FAA CPC). */
  postCertSalary?: number;
}

export interface CalculatorOutput {
  /** Tier dollar cost across the study window (not per-month). */
  totalCost: number;
  /** Effective hourly cost of the subscription. */
  hourlyCost: number;
  /** Estimated total hours studied over the window. */
  totalHours: number;
  /** Expected first-year salary value the washout reduction unlocks. */
  expectedSalaryValue: number;
  /** Multiple by which expected salary value exceeds tier cost. */
  evMultiple: number;
}

// Tier dollar costs in cents — kept here (not imported from pricing.ts)
// so the calculator math is independent and stable for testing. If
// pricing changes we update both.
const TIER_COST_CENTS: Record<TierChoice, number> = {
  trainee_yearly: 8900, // $89/yr
  pro_yearly: 24900, // $249/yr
};

const TIER_DURATION_MONTHS: Record<TierChoice, number> = {
  trainee_yearly: 12,
  pro_yearly: 12,
};

const DEFAULT_RISK_REDUCTION_POINTS = 1;
// CY2024 published FAA Air Traffic Controller (CPC) average. Round
// number kept so the calculator's output reads cleanly; this is an
// assumption surfaced to the user, not a quoted figure.
const DEFAULT_POST_CERT_SALARY = 128_000;

export function calculate(input: CalculatorInput): CalculatorOutput {
  const months = clampPositive(input.monthsUntilTest, 1);
  const dailyMin = clampPositive(input.dailyStudyMinutes, 5);
  const reductionPts = clampPositive(
    input.washoutRiskReductionPoints ?? DEFAULT_RISK_REDUCTION_POINTS,
    0,
  );
  const salary = clampPositive(
    input.postCertSalary ?? DEFAULT_POST_CERT_SALARY,
    0,
  );

  const duration = TIER_DURATION_MONTHS[input.tier];

  // Total cost: yearly tiers are billed once per 12 months over the
  // study window (rounded up — Stripe doesn't refund partial periods).
  const baseCents = TIER_COST_CENTS[input.tier];
  const totalCents = baseCents * Math.max(1, Math.ceil(months / duration));
  const totalCost = totalCents / 100;

  // Hours: 30 days/month is the right granularity for a months-out
  // estimate. Daily minutes → hours → multiply by days in window.
  const totalHours = (dailyMin / 60) * 30 * months;
  const hourlyCost = totalHours > 0 ? totalCost / totalHours : 0;

  // Expected salary value: salary × risk-reduction probability.
  // 1pt reduction at a 30% washout baseline = .01 absolute reduction
  // = ~3.3% relative — that's the framing we surface in copy.
  const expectedSalaryValue = salary * (reductionPts / 100);
  const evMultiple = totalCost > 0 ? expectedSalaryValue / totalCost : 0;

  return {
    totalCost,
    hourlyCost,
    totalHours,
    expectedSalaryValue,
    evMultiple,
  };
}

function clampPositive(value: number, fallback: number): number {
  if (!Number.isFinite(value) || value <= 0) return fallback;
  return value;
}

export function formatUsd(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: amount >= 100 ? 0 : 2,
  }).format(amount);
}

export function formatHours(hours: number): string {
  if (hours < 10) return `${hours.toFixed(1)} hrs`;
  return `${Math.round(hours).toLocaleString()} hrs`;
}
